import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { PostContext } from '../types/chat';
import { Send, Trash2, MessageCircle, X, Loader2, Mic, Paperclip, RefreshCw } from 'lucide-react';
import { usePostChat } from '../hooks/usePostChat';
import { ChatQuickActions } from './ChatQuickActions';
import { PostSuggestionPreview } from './PostSuggestionPreview';
import { AudioMessagePlayer } from './AudioMessagePlayer';
import { useVoiceVisualizer, VoiceVisualizer } from 'react-voice-visualizer';
import { presignPostChatAudio, presignPostChatImages, uploadBlobToPresignedPut } from '../services/mediaService';
import { transcodeToWavPcm16 } from '../utils/ffmpegAudio';
import { CarouselTemplateSelector } from './carousel/CarouselTemplateSelector';
import { CarouselTemplate } from '../services/carouselTemplateService';
import { useAuth } from '../context/AuthContext';

interface PostChatProps {
  contentPlanId: string;
  postId?: string;
  postContext: PostContext;
  onPostUpdate: (updates: any) => void;
  onRevertField?: (field: string) => void; // Новая функция для отмены изменений
  originalValues?: Partial<PostContext>; // Исходные значения полей
  appliedChanges?: Record<string, boolean>; // Отслеживание примененных изменений
  onSwitchToPreview?: () => void; // Функция для переключения на вкладку превью
  className?: string;
}



// Компонент модального окна подтверждения
const ConfirmModal: React.FC<{
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}> = ({ isOpen, onConfirm, onCancel, title, message, confirmText = 'Удалить', cancelText = 'Отмена' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{title}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
          
          <div className="flex space-x-3 justify-end">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PostChat: React.FC<PostChatProps> = ({
  contentPlanId,
  postId,
  postContext,
  onPostUpdate,
  onRevertField,
  originalValues,
  appliedChanges,
  onSwitchToPreview,
  className = ''
}) => {
  const { subscription } = useAuth();
  const [inputMessage, setInputMessage] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [stagedImageUrls, setStagedImageUrls] = useState<(string | null)[]>([]); // уже загружены в S3, но еще не отправлены в чат
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [imageUiError, setImageUiError] = useState<string | null>(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalData, setConfirmModalData] = useState({ title: '', message: '' });
  const [isSendingAudio, setIsSendingAudio] = useState(false);
  const [audioUiError, setAudioUiError] = useState<string | null>(null);
  const [autoSendAfterStop, setAutoSendAfterStop] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const processingWatchdogRef = useRef<number | null>(null);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CarouselTemplate | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const effectivePostContext: PostContext = useMemo(() => {
    return {
      ...postContext,
      carouselTemplateId: selectedTemplate?.id || undefined
    };
  }, [postContext, selectedTemplate]);

  // Используем новый хук для управления чатом
  const {
    messages,
    isTyping,
    isConnected,
    isLoading,
    isLoadingHistory,
    error,
    sendMessage,
    regenerateMessage,
    sendAudioMessage,
    deleteMessage,
    clearHistory,
    clearError,
    updateMessageContent,
    updatePostIdInMessages,
    reconnect,
    hasMessages
  } = usePostChat({
    contentPlanId,
    postId,
    postContext: effectivePostContext,
    onPostUpdate
  });

  // Если чат начинался на временном postId, а потом пост сохранили и появился реальный id —
  // мигрируем историю сообщений, чтобы "редактирование из чата" работало и история не терялась.
  const tempPostIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (postId && postId.startsWith('temp_')) {
      tempPostIdRef.current = postId;
    }
  }, [postId]);

  useEffect(() => {
    const run = async () => {
      if (!isConnected) return;
      if (!postId) return;
      if (postId.startsWith('temp_')) return;
      const oldTemp = tempPostIdRef.current;
      if (!oldTemp || oldTemp === postId) return;
      const ok = await updatePostIdInMessages(oldTemp, postId);
      if (!ok) return;
      tempPostIdRef.current = null;
      // Перезагрузим историю уже по новому postId
      await reconnect();
    };
    void run();
  }, [isConnected, postId, updatePostIdInMessages, reconnect]);

  // Автоскролл к последнему сообщению
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);



  // Показываем быстрые действия, если нет сообщений
  useEffect(() => {
    setShowQuickActions(!hasMessages && isConnected);
  }, [hasMessages, isConnected]);

  // Автоматическое изменение высоты textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 120);
      textarea.style.height = newHeight + 'px';
    }
  }, [inputMessage]);

  // Голосовые сообщения (react-voice-visualizer)
  const recorderControls = useVoiceVisualizer({ shouldHandleBeforeUnload: false });
  const {
    isRecordingInProgress,
    recordedBlob,
    isProcessingRecordedAudio,
    recordingTime,
    duration,
    startRecording,
    stopRecording,
    clearCanvas,
    error: recorderError
  } = recorderControls;

  const hasTypedText = inputMessage.trim().length > 0;
  const hasRecordedBlob = !!recordedBlob;
  const hasSelectedImages = selectedImages.length > 0;
  // Важно: держим аудио-UI смонтированным, пока формируется blob (иначе можно потерять recordedBlob)
  const showAudioUI =
    !hasTypedText &&
    (isRecordingInProgress || autoSendAfterStop || isProcessingRecordedAudio || hasRecordedBlob);

  const isProTariff = useMemo(() => {
    // Pro тариф из требований
    const PRO_TARIFF_ID = 'a711c773-f677-4f6f-9d6e-0e1b1f13ddc7';
    // Специальный план с доступом к загрузке файлов
    const ALLOWED_PLAN_ID = '51f00fb3-e5ab-4442-bf77-5da5f3cf8f9f';
    return subscription?.isActive === true && 
           (subscription?.tariffPlanId === PRO_TARIFF_ID || subscription?.tariffPlanId === ALLOWED_PLAN_ID);
  }, [subscription?.isActive, subscription?.tariffPlanId]);

  const handlePickImages = useCallback(() => {
    setImageUiError(null);
    if (!isProTariff) return;
    if (!imageInputRef.current) return;
    imageInputRef.current.click();
  }, [isProTariff]);

  const handleImagesSelected = useCallback(async (files: FileList | null) => {
    setImageUiError(null);
    if (!files || files.length === 0) return;

    const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml']);
    const incoming = Array.from(files);
    const invalid = incoming.filter(f => !allowedTypes.has((f.type || '').toLowerCase()));
    if (invalid.length > 0) {
      setImageUiError('Можно прикреплять только изображения PNG/JPG/WebP/SVG.');
      return;
    }

    const currentCount = selectedImages.length;
    const canTake = 4 - currentCount;
    if (canTake <= 0) {
        setImageUiError('Можно прикрепить максимум 4 изображения.');
      return;
    }

    const toAdd = incoming.slice(0, canTake);
    if (incoming.length > canTake) {
      setImageUiError('Можно прикрепить максимум 4 изображения.');
    }

    const newFiles = [...selectedImages, ...toAdd];
    setSelectedImages(newFiles);
    
    // Инициализируем null для новых файлов
    setStagedImageUrls(prev => [...prev, ...new Array(toAdd.length).fill(null)]);

    setIsUploadingImages(true);
    setImageUploadProgress({ done: 0, total: toAdd.length });

    try {
      const contentTypes = toAdd.map(f => f.type || 'image/jpeg');
      const presigned = await presignPostChatImages({
        contentPlanId,
        postId,
        contentTypes
      });

      if (!presigned.images || presigned.images.length !== toAdd.length) {
        throw new Error('Не удалось подготовить загрузку изображений.');
      }

      for (let i = 0; i < toAdd.length; i++) {
        const file = toAdd[i];
        const item = presigned.images[i];
        await uploadBlobToPresignedPut(item.uploadUrl, file, file.type || 'image/jpeg');
        
        const url = item.fileUrl;
        setStagedImageUrls(prev => {
          const updated = [...prev];
          // Индекс в общем массиве = currentCount + i
          if (updated.length > currentCount + i) {
            updated[currentCount + i] = url;
          }
          return updated;
        });
        setImageUploadProgress({ done: i + 1, total: toAdd.length });
      }
    } catch (e: any) {
      console.error('[PostChat] Instant upload error:', e);
      const msg = String(e?.message || e || '');
      if (msg.includes('HTTP 403') || msg.toLowerCase().includes('forbid')) {
        setImageUiError('Загрузка изображений доступна только на тарифе Pro (или нет доступа к контент-плану).');
      } else {
        setImageUiError('Не удалось загрузить изображения. Проверь соединение и попробуй ещё раз.');
      }
    } finally {
      setIsUploadingImages(false);
    }
  }, [selectedImages, contentPlanId, postId]);

  useEffect(() => {
    // Генерируем превью через object URLs и чистим их при изменении/размонте
    const urls = selectedImages.map((f) => URL.createObjectURL(f));
    setImagePreviewUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [selectedImages]);

  const handleRemoveSelectedImage = useCallback((index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setStagedImageUrls((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Watchdog: если blob "обрабатывается" слишком долго — покажем ошибку (иначе пользователь видит тишину)
  useEffect(() => {
    if (!isProcessingRecordedAudio) {
      if (processingWatchdogRef.current) {
        window.clearTimeout(processingWatchdogRef.current);
        processingWatchdogRef.current = null;
      }
      return;
    }
    if (typeof window === 'undefined') return;
    if (processingWatchdogRef.current) return;
    processingWatchdogRef.current = window.setTimeout(() => {
      setAudioUiError('Не удалось обработать запись голосового. Попробуйте перезаписать и отправить ещё раз.');
      processingWatchdogRef.current = null;
    }, 12000);
    return () => {
      if (processingWatchdogRef.current) {
        window.clearTimeout(processingWatchdogRef.current);
        processingWatchdogRef.current = null;
      }
    };
  }, [isProcessingRecordedAudio, recordedBlob]);

  const formatDurationMs = (ms?: number | null) => {
    if (ms === undefined || ms === null || ms <= 0) return '00:00';
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSendRecordedAudio = useCallback(async (blobToSend?: Blob) => {
    let blob = blobToSend || recordedBlob;
    if (!blob || isSendingAudio) return;

    try {
      setAudioUiError(null);
      setIsSendingAudio(true);
      // Если запись еще идет, сначала остановим
      if (isRecordingInProgress) {
        stopRecording();
      }

      // MediaRecorder чаще всего пишет webm/opus, а роутер input_audio webm не поддерживает.
      // Поэтому перекодируем в WAV (PCM16) через ffmpeg.wasm.
      let contentType = blob.type || 'audio/webm';
      console.log('[PostChat] Тип входного аудио:', contentType, 'Размер:', blob.size);
      
      if (/webm/i.test(contentType) || /audio\/webm/i.test(contentType)) {
        console.log('[PostChat] Обнаружен webm, начинаем конвертацию...');
        try {
          blob = await transcodeToWavPcm16(blob);
          contentType = 'audio/wav';
          console.log('[PostChat] Конвертация успешна! Новый тип:', contentType, 'Размер:', blob.size);
        } catch (convError: any) {
          const convMsg = String(convError?.message || convError || '');
          console.error('[PostChat] Ошибка конвертации:', convError);
          if (/ffmpeg|module|Cannot find|не установлен/i.test(convMsg)) {
            setAudioUiError('Ошибка конвертации аудио: ffmpeg.wasm не установлен. Установите пакеты: npm install @ffmpeg/ffmpeg @ffmpeg/util');
            return;
          }
          // Если конвертация не удалась, но это не критично - можно попробовать отправить как есть
          // Но лучше показать ошибку пользователю
          setAudioUiError(`Ошибка конвертации аудио: ${convMsg}. Проверьте консоль браузера.`);
          return;
        }
      } else {
        console.log('[PostChat] Аудио не webm, пропускаем конвертацию. Тип:', contentType);
      }
      // Используем duration если есть, иначе время записи
      const finalDuration = (duration > 0 ? duration : recordingTime / 1000) * 1000;

      const presign = await presignPostChatAudio({
        contentPlanId,
        postId,
        contentType,
        durationMs: Math.round(finalDuration)
      });

      if (!presign.uploadUrl || !presign.key || !presign.fileUrl) {
        setAudioUiError('Не удалось подготовить загрузку голосового (ошибка сервера).');
        return;
      }

      await uploadBlobToPresignedPut(presign.uploadUrl, blob, contentType);

      const ok = await sendAudioMessage({
        audioKey: presign.key,
        audioUrl: presign.fileUrl,
        audioMimeType: contentType,
        durationMs: Math.round(finalDuration)
      });
      
      if (!ok) {
        setAudioUiError('Не удалось отправить голосовое сообщение. Попробуйте ещё раз.');
        return;
      }

      // Успешно отправили — очищаем всё
      clearCanvas();
      setAutoSendAfterStop(false);
      
    } catch (e) {
      const msg = String((e as any)?.message || e || '');
      if ((e as any)?.name === 'AbortError' || /aborted|timeout/i.test(msg)) {
        setAudioUiError('Загрузка голосового заняла слишком много времени. Проверьте интернет и попробуйте ещё раз.');
      } else if (/S3 upload failed/i.test(msg)) {
        setAudioUiError('Не удалось загрузить голосовое в хранилище. Попробуйте ещё раз (или позже).');
      } else if (/forbid|unauthoriz/i.test(msg)) {
        setAudioUiError('Нет доступа к отправке голосовых (проверьте подписку/авторизацию).');
      } else if (/ffmpeg|конвертац/i.test(msg)) {
        setAudioUiError('Ошибка конвертации аудио. Проверьте консоль браузера.');
      } else {
        setAudioUiError('Не удалось отправить голосовое сообщение.');
      }
    } finally {
      setIsSendingAudio(false);
      setAutoSendAfterStop(false); // На всякий случай сбрасываем флаг авто-отправки
    }
  }, [
    recordedBlob,
    isSendingAudio,
    isRecordingInProgress,
    stopRecording,
    duration,
    recordingTime,
    contentPlanId,
    postId,
    sendAudioMessage,
    clearCanvas,
  ]);

  // Авто-отправка после остановки записи
  useEffect(() => {
    if (autoSendAfterStop && recordedBlob && !isProcessingRecordedAudio && !isRecordingInProgress) {
      handleSendRecordedAudio(recordedBlob);
    }
  }, [autoSendAfterStop, recordedBlob, isProcessingRecordedAudio, isRecordingInProgress, handleSendRecordedAudio]);

  useEffect(() => {
    if (!recorderError) return;
    
    const msg = recorderError.message || '';
    if (/denied|notallowed|permission/i.test(msg)) {
      setAudioUiError('Нет доступа к микрофону. Разреши микрофон в настройках браузера.');
    } else {
      setAudioUiError(msg || 'Ошибка записи аудио');
    }
  }, [recorderError]);

  // Скрытие ошибки аудио через 5 секунд
  useEffect(() => {
    if (!audioUiError) return;
    
    const timer = setTimeout(() => {
      setAudioUiError(null);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [audioUiError]);

  const handleStartRecording = async () => {
    setAudioUiError(null);

    // Блокируем запись в небезопасном контексте (кроме localhost)
    if (typeof window !== 'undefined') {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const isSecure = window.location.protocol === 'https:';
      if (!isSecure && !isLocalhost) {
        setAudioUiError('Запись доступна только по HTTPS (или на localhost).');
        return;
      }
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setAudioUiError('Браузер не поддерживает запись с микрофона (нет getUserMedia).');
      return;
    }

    try {
      // Явно запрашиваем разрешение, чтобы пользователь увидел системный диалог
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());

      startRecording();
    } catch (e: any) {
      const msg = String(e?.message || e || '');
      if (/denied|notallowed|permission/i.test(msg)) {
        setAudioUiError('Нет доступа к микрофону. Разреши микрофон в настройках браузера.');
        return;
      }
      setAudioUiError('Не удалось запустить запись. Проверь доступ к микрофону и браузер.');
    }
  };

  // Автопереподключение: не показываем ошибку пользователю, пробуем переподключаться
  useEffect(() => {
    // Если подключены — сбрасываем попытки и таймер
    if (isConnected) {
      setRetryAttempt(0);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }

    // Если не подключены и не идет загрузка — ставим таймер переподключения
    if (!isConnected && !isLoading && !isLoadingHistory) {
      const attempt = retryAttempt + 1;
      setRetryAttempt(attempt);
      // экспоненциальная задержка с потолком 10 сек
      const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        reconnect();
      }, delayMs);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [isConnected, isLoading, isLoadingHistory, reconnect]);

  const handleSendMessage = async (message?: string) => {
    const messageToSend = message || inputMessage;
    if (hasSelectedImages && !messageToSend.trim()) {
      setImageUiError('Нельзя отправлять изображения без текста.');
      return;
    }
    if (!messageToSend.trim() || isTyping) {
      return;
    }

    // Если есть вложения — проверяем, что все загружены
    let uploadedImageUrls: string[] | null = null;
    if (hasSelectedImages) {
      // Отфильтровываем null и проверяем, что количество совпадает
      const readyUrls = stagedImageUrls.filter((url): url is string => url !== null);
      
      if (readyUrls.length !== selectedImages.length) {
        setImageUiError('Пожалуйста, дождитесь окончания загрузки всех изображений.');
            return;
          }
      uploadedImageUrls = readyUrls;
    }

    // Снимок текущих вложений (для восстановления при ошибке)
    const prevSelectedImages = selectedImages;
    const prevStagedImageUrls = stagedImageUrls;
    const prevImageUploadProgress = imageUploadProgress;
    const prevImageUiError = imageUiError;

    // Очищаем поле ввода сразу при отправке
    if (!message) {
      setInputMessage('');
    }

    // Вложения очищаем оптимистично сразу после клика, а не после ответа ИИ.
    // Иначе пользователь видит "сообщение ушло", но превью висит до конца генерации на бэке.
    if (hasSelectedImages) {
      setSelectedImages([]);
      setStagedImageUrls([]);
      setImagePreviewUrls([]);
      setImageUploadProgress({ done: 0, total: 0 });
      setImageUiError(null);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }

    const success = await sendMessage(messageToSend, uploadedImageUrls);
    if (success) {
      // Скрываем быстрые действия только после успешной отправки сообщения
      if (!hasMessages) {
        setShowQuickActions(false);
      }
    } else {
      // Если отправка не удалась, возвращаем текст обратно в поле
      if (!message) {
        setInputMessage(messageToSend);
      }
      // И возвращаем вложения (чтобы пользователь не потерял их из-за сетевой ошибки)
      if (prevSelectedImages.length > 0) {
        setSelectedImages(prevSelectedImages);
        setStagedImageUrls(prevStagedImageUrls);
        setImageUploadProgress(prevImageUploadProgress);
        setImageUiError(prevImageUiError);
      }
    }
  };

  const handleQuickAction = (message: string) => {
    handleSendMessage(message);
  };

  // const isCarousel = (postContext.format || '').toLowerCase().includes('carousel') || (postContext.format || '').toLowerCase().includes('карусель');

  const handleDeleteMessage = async (messageId: string) => {
    setConfirmModalData({
      title: 'Удалить сообщение',
      message: 'Вы уверены, что хотите удалить это сообщение? Это действие нельзя отменить.'
    });
    setConfirmAction(() => async () => {
      await deleteMessage(messageId);
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };

  const handleRegenerateMessage = async (messageId: string) => {
    // Регенерация только для сообщений ИИ
    const currentMessage = messages.find(m => m.id === messageId);
    if (!currentMessage?.isFromAI) return;

    // Аудио пока не поддерживаем (на бэке нет ключа для повторной обработки)
    if (currentMessage.messageType === 'audio' && currentMessage.audioUrl) {
      return;
    }

    await regenerateMessage(messageId);
  };

  const handleClearHistory = async () => {
    setConfirmModalData({
      title: 'Очистить историю чата',
      message: 'Вы уверены, что хотите очистить всю историю чата? Это действие нельзя отменить.'
    });
    setConfirmAction(() => async () => {
      await clearHistory();
      setShowQuickActions(true);
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageTime = (timestamp: string) => {
    // Проверяем, что timestamp не пустой и не undefined
    if (!timestamp) {
      return 'Сейчас';
    }

    // Пытаемся создать дату из timestamp
    let date = new Date(timestamp);
    
    // Если дата невалидна, пробуем другие форматы
    if (isNaN(date.getTime())) {
      // Пробуем парсить как ISO строку
      date = new Date(timestamp.replace(' ', 'T'));
      
      // Если все еще невалидна, возвращаем текущее время
      if (isNaN(date.getTime())) {
        return 'Сейчас';
      }
    }

    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    try {
      if (diffInHours < 24) {
        return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      } else {
        return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) + ' ' + 
               date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      }
    } catch (error) {
      // Если форматирование не удалось, возвращаем базовую строку
      return 'Недавно';
    }
  };

  return (
    <>
      <div className={`flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-md ${className}`}>
        {/* Заголовок чата */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              <h3 className="font-medium text-gray-900 dark:text-white">Чат с ИИ</h3>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            </div>

            <div className="flex items-center gap-2">
              {(() => {
                const format = (postContext.format || '').toLowerCase().trim();
                const isCarousel = format.includes('carousel') || format.includes('карусель');
                if (!isCarousel) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setIsTemplatesOpen(true)}
                    className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Выбрать шаблон карусели"
                  >
                    Шаблоны
                  </button>
                );
              })()}

              <button
                onClick={handleClearHistory}
                disabled={!isConnected || !hasMessages}
                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Очистить историю чата"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {(() => {
            const format = (postContext.format || '').toLowerCase().trim();
            const isCarousel = format.includes('carousel') || format.includes('карусель');
            if (!isCarousel || !selectedTemplate) return null;
            return (
              <div className="mt-2 flex items-center gap-2">
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  Стиль: <span className="font-semibold">{selectedTemplate.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedTemplate(null)}
                  className="text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white underline"
                  aria-label="Сбросить выбранный шаблон"
                >
                  Сбросить
                </button>
              </div>
            );
          })()}
        </div>

        {/* Сообщения */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Отображение ошибки */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start justify-between">
              <p className="text-sm text-red-800 dark:text-red-200 flex-1">{error}</p>
              <button
                onClick={clearError}
                className="ml-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                aria-label="Закрыть"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {isLoadingHistory ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />
              <p className="text-lg font-medium mb-2">Загружаем историю чата...</p>
              <p className="text-sm">Пожалуйста, подождите</p>
            </div>
          ) : !hasMessages ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              {/* Не показываем ошибку пользователю, просто индикатор подключения */}
              {!isConnected ? (
                <>
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />
                  <p className="text-lg font-medium mb-2">Подключение к чату...</p>
                  <p className="text-sm">Пожалуйста, подождите</p>
                </>
              ) : (
                <>
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg font-medium mb-2">Начните диалог с ИИ</p>
                  <p className="text-sm mb-6">Задайте вопрос или попросите улучшить ваш пост</p>
                  
                  {/* Быстрые действия */}
                  {showQuickActions && isConnected && (
                    <div className="max-w-md mx-auto">
                      <p className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Или выберите готовый вариант:</p>
                      <ChatQuickActions 
                        onSendMessage={handleQuickAction}
                        format={postContext.format}
                        disabled={!isConnected || isTyping || isLoadingHistory}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            messages.filter(message => message.content || message.id).map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isFromAI ? 'justify-start' : 'justify-end'}`}
              >
                <div className="relative group max-w-xl min-w-0">
                  <div
                    className={`rounded-2xl w-full ${
                      message.messageType === 'audio' ? 'px-2 py-1' : 'px-4 py-2'
                    } ${
                      message.isFromAI
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : message.isFromCurrentUser
                          ? 'bg-blue-500 dark:bg-slate-500 text-white'
                          : 'bg-indigo-500 dark:bg-indigo-600 text-white'
                    }`}
                  >
                    {message.messageType === 'audio' && message.audioUrl ? (
                      <AudioMessagePlayer
                        audioUrl={message.audioUrl}
                        durationMs={message.durationMs ?? undefined}
                        isFromAI={message.isFromAI}
                        timestamp={formatMessageTime(message.timestamp)}
                      />
                    ) : (
                      <>
                        {Array.isArray((message as any).imageUrls) && (message as any).imageUrls.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-1.5">
                            {(message as any).imageUrls.slice(0, 4).map((url: string) => (
                              <div key={url} className="w-20 h-20 rounded-lg overflow-hidden border border-white/20 bg-white/10 flex-shrink-0">
                                <img
                                  src={url}
                                  alt="Вложение"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Отображение сообщения с парсингом SUGGEST тегов */}
                        <PostSuggestionPreview
                          messageId={message.id}
                          messageContent={message.content || ''}
                          onApply={(suggestions) => {
                            // Применяем предложения к посту
                            onPostUpdate(suggestions);
                          }}
                          onUpdateMessageContent={(messageId, nextContent) => {
                            updateMessageContent(messageId, nextContent);
                          }}
                          onRevert={onRevertField}
                          originalValues={originalValues}
                          appliedChanges={appliedChanges}
                          onSwitchToPreview={onSwitchToPreview}
                          disabled={!isConnected || isTyping}
                        />
                      </>
                    )}
                    
                    <div className="flex items-center justify-between mt-1">
                      {message.messageType !== 'audio' && (
                      <p className="text-xs opacity-70">
                        {formatMessageTime(message.timestamp)}
                      </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Действия под сообщением */}
                  <div className={`flex items-center space-x-2 mt-1 ${message.isFromAI ? 'justify-start' : 'justify-end'}`}>
                    <button
                      onClick={() => handleDeleteMessage(message.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Удалить сообщение"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {message.isFromAI && (
                      <button
                        onClick={() => handleRegenerateMessage(message.id)}
                        className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors"
                        title="Регенерировать ответ"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Индикатор печати ИИ */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full typing-indicator-dot"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full typing-indicator-dot"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full typing-indicator-dot"></div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400"></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Поле ввода */}
        <div className="pt-2 px-2 pb-1 md:p-4">
          {/* Превью выбранных изображений */}
          {hasSelectedImages && !showAudioUI && (
            <div className="mb-2 px-1">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  Прикреплено: <span className="font-semibold">{selectedImages.length}</span>/4
                  {isUploadingImages && (
                    <span className="ml-2 text-blue-600 dark:text-blue-300">
                      Загрузка {imageUploadProgress.done}/{imageUploadProgress.total}…
                    </span>
                  )}
                  {!isUploadingImages && stagedImageUrls.length > 0 && stagedImageUrls.every(url => url !== null) && (
                    <span className="ml-2 text-green-600 dark:text-green-400">
                      Загружено
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImages([]);
                    setStagedImageUrls([]);
                    setImagePreviewUrls([]);
                    setImageUploadProgress({ done: 0, total: 0 });
                    setImageUiError(null);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white underline"
                  aria-label="Удалить все прикрепленные изображения"
                >
                  Очистить
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {imagePreviewUrls.map((url, idx) => (
                  <div key={url} className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <img
                      src={url}
                      alt={`Выбранное изображение ${idx + 1}`}
                      className="w-full h-full object-cover bg-gray-100 dark:bg-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveSelectedImage(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 hover:bg-black/75 text-white flex items-center justify-center transition-colors"
                      aria-label={`Удалить изображение ${idx + 1}`}
                      title="Удалить"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-end space-x-2">
            {showAudioUI ? (
              <div
                className="flex-1 rounded-full px-4 py-1
                           bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-200 min-h-[44px] flex flex-row items-center gap-3 border border-gray-200 dark:border-gray-700"
              >
                <div className="text-sm font-medium min-w-[45px] text-gray-600 dark:text-gray-400">
                  {isProcessingRecordedAudio ? '...' : formatDurationMs(isRecordingInProgress ? recordingTime : duration * 1000)}
                </div>
                <div className="flex-1 h-10 flex items-center overflow-hidden">
                  <VoiceVisualizer
                    controls={recorderControls}
                    height={40}
                    barWidth={4}
                    gap={3}
                    rounded={12}
                    isControlPanelShown={false}
                    isDefaultUIShown={false}
                    onlyRecording={false}
                    backgroundColor="transparent"
                    mainBarColor="#3b82f6"
                    secondaryBarColor="#93c5fd"
                  />
                </div>
              </div>
            ) : (
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                isLoadingHistory ? "Загружается история чата..." :
                isConnected ? "Напишите сообщение..." : "Подключение..."
              }
                disabled={!isConnected || isLoadingHistory || isSendingAudio}
              rows={1}
                className="flex-1 resize-none border border-gray-300 dark:border-gray-600 rounded-2xl px-4 py-2 text-base 
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                       disabled:opacity-50 disabled:cursor-not-allowed
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         placeholder-gray-400 dark:placeholder-gray-500 min-h-[44px] max-h-[120px]"
              style={{ height: 'auto' }}
            />
            )}

            {/* Вложения (Pro) */}
            <div className="relative flex items-center">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleImagesSelected(e.target.files);
                  // сброс value, чтобы можно было выбрать те же файлы повторно
                  e.currentTarget.value = '';
                }}
              />
              <button
                type="button"
                onClick={handlePickImages}
                disabled={!isConnected || isLoadingHistory || isSendingAudio || showAudioUI || !isProTariff}
                className="relative w-11 h-11 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-all flex items-center justify-center flex-shrink-0 shadow-sm rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label={isProTariff ? 'Прикрепить изображения (до 4)' : 'Прикрепление изображений доступно на тарифе Pro'}
                title={isProTariff ? 'Прикрепить изображения (до 4)' : 'Доступно на тарифе Pro'}
              >
                <Paperclip className="w-5 h-5" />
              </button>

              {!isProTariff && (
                <div className="absolute -top-1 -right-1 pointer-events-none">
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white shadow">
                    PRO
                  </span>
                </div>
              )}
            </div>
            
              <div className="flex items-center">
                <button
                  onClick={() => {
                    if (isSendingAudio || isLoadingHistory) return;
                    if (isUploadingImages) return;
                    if (hasSelectedImages && !hasTypedText) {
                      setImageUiError('Нельзя отправлять изображения без текста.');
                      return;
                    }
                    if (isRecordingInProgress) {
                      setAutoSendAfterStop(true);
                      stopRecording();
                      return;
                    }
                    if (recordedBlob) {
                      handleSendRecordedAudio();
                      return;
                    }
                    if (hasTypedText) {
                      handleSendMessage();
                      return;
                    }
                    if (hasSelectedImages) {
                      // Если прикреплены изображения, голосовую запись не запускаем
                      setImageUiError('Добавьте текст, чтобы отправить сообщение с изображениями.');
                      return;
                    }
                    handleStartRecording();
                  }}
                  disabled={isLoadingHistory || isSendingAudio || isProcessingRecordedAudio || isUploadingImages || (hasSelectedImages && !hasTypedText)}
                  className="w-11 h-11 bg-blue-500 hover:bg-blue-600 text-white transition-all flex items-center justify-center flex-shrink-0 shadow-sm
                           rounded-full disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                  title={
                    isSendingAudio ? "Отправка..." :
                    isUploadingImages ? "Загрузка изображений..." :
                    hasSelectedImages && !hasTypedText ? "Введите текст, чтобы отправить изображения" :
                    hasTypedText ? "Отправить" :
                    isRecordingInProgress ? "Отправить голосовое" :
                    recordedBlob ? "Отправить голосовое" :
                    "Начать запись"
                  }
                >
                  {isSendingAudio || isProcessingRecordedAudio ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (hasSelectedImages || hasTypedText || isRecordingInProgress || recordedBlob) ? (
                    <Send className="w-5 h-5 -translate-x-px" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
              </div>
          </div>
          {audioUiError && (
            <div className="mt-2 px-4 text-xs text-red-600 dark:text-red-400 italic">
              {audioUiError}
            </div>
          )}
          {imageUiError && (
            <div className="mt-2 px-4 text-xs text-red-600 dark:text-red-400 italic">
              {imageUiError}
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно подтверждения */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={confirmAction || (() => {})}
        onCancel={() => setShowConfirmModal(false)}
        title={confirmModalData.title}
        message={confirmModalData.message}
      />

      <CarouselTemplateSelector
        open={isTemplatesOpen}
        selectedTemplateId={selectedTemplate?.id || null}
        onClose={() => setIsTemplatesOpen(false)}
        onSelect={(t) => {
          setSelectedTemplate(t);
          setIsTemplatesOpen(false);
        }}
      />
    </>
  );
}; 